import React from "react";
import { Card, Empty, List, Space, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { EventUserStatistics } from "../../../services/drinkService";
import { formatAlcohol, formatVolume } from "./utils";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

export type OngoingEventLeaderboardEntry = {
    event: {
        eventId: number;
        eventName: string;
        start?: Date | null;
    };
    topUsers: EventUserStatistics[];
    totalAlcoholGrams: number;
};

type OngoingEventsCardProps = {
    loading: boolean;
    leaderboards: OngoingEventLeaderboardEntry[];
};

const OngoingEventsCard: React.FC<OngoingEventsCardProps> = ({ loading, leaderboards }) => {
    const { t } = useTranslation();

    if (!loading && leaderboards.length === 0) {
        return null;
    }

    return (
        <Card title={t("eventStats.ongoing.title")}>
            <Spin spinning={loading}>
                {leaderboards.length ? (
                    <List
                        dataSource={leaderboards}
                        renderItem={(entry) => (
                            <List.Item key={entry.event.eventId}>
                                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                                    <Text strong>{entry.event.eventName}</Text>
                                    <Text type="secondary">
                                        {entry.event.start
                                            ? t("eventStats.ongoing.started", {
                                                  time: dayjs(entry.event.start).format("MMM D HH:mm"),
                                              })
                                            : t("eventStats.ongoing.startUnknown")}
                                    </Text>
                                    {entry.topUsers.length ? (
                                        entry.topUsers.map((participant, index) => (
                                            <Text key={`${entry.event.eventId}-${participant.userId}`}>
                                                {index + 1}. {participant.username === "Unknown" ? t("common.unknownUser") : participant.username} ·{" "}
                                                {formatAlcohol(participant.totalAlcoholGrams)} ·{" "}
                                                {formatVolume(participant.totalVolumeMl)}
                                            </Text>
                                        ))
                                    ) : (
                                        <Text type="secondary">{t("eventStats.ongoing.noDrinks")}</Text>
                                    )}
                                </Space>
                            </List.Item>
                        )}
                    />
                ) : (
                    <Empty
                        description={t("eventStats.ongoing.empty")}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Spin>
        </Card>
    );
};

export default OngoingEventsCard;
